import { createSlice } from '@reduxjs/toolkit';
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { updateError } from './user';

const initialState = {
    sessionKey: null, practiceId: null, accountId: null, attemptId: null, generation: 0,
    tiktokAccount: null, tiktokAdvertisers: [], tiktokLeadGenForms: [], availableAdvertisers: [],
    pendingAttempts: [], canManageConnection: false, isLoading: false, isConnecting: false, error: null,
    formsAdvertiserId: null, formsGeneration: 0,
};
const scopeFields = ['sessionKey', 'practiceId', 'accountId', 'attemptId', 'generation'];
export const selectTikTokScope = (state) => Object.fromEntries(scopeFields.map(key => [key, state.tiktokIntegration[key]]));
export const sameTikTokScope = (a, b) => !!a && !!b && scopeFields.every(key => a[key] === b[key]);
const controllers = new Set();
export const cancelTikTokRequests = () => { controllers.forEach(controller => controller.abort()); controllers.clear(); };
export const createTikTokSessionKey = () => window.crypto.randomUUID();
export const tikTokScopeMiddleware = ({ getState }) => next => action => {
    const scope = selectTikTokScope(getState());
    const result = next(action);
    if (!sameTikTokScope(scope, selectTikTokScope(getState()))) cancelTikTokRequests();
    return result;
};

// Fixed messages only: provider responses and Axios configs must never enter Redux/Sentry.
export function safeTikTokError(error) {
    const status = error?.status || error?.response?.status;
    if (status === 403) return 'You do not have permission to manage this TikTok connection.';
    if (status === 409) return 'The TikTok connection changed. Reload Integrations and try again.';
    if (status === 400 || status === 404) return 'TikTok setup is unavailable or expired. Reconnect TikTok to try again.';
    return 'Unable to complete the TikTok request. Please try again.';
}

const slice = createSlice({
    name: 'tiktokIntegration', initialState,
    reducers: {
        resetTikTokScope(state, { payload }) {
            return { ...initialState, sessionKey: payload.sessionKey, practiceId: payload.practiceId, generation: state.generation + 1 };
        },
        formsOpened(state, { payload }) {
            state.formsAdvertiserId = payload;
            state.formsGeneration += 1;
            state.tiktokLeadGenForms = [];
        },
        selectionOpened(state, { payload }) {
            state.generation += 1;
            state.accountId = payload.accountId ?? null;
            state.attemptId = payload.attemptId ?? null;
            state.availableAdvertisers = [];
            state.tiktokLeadGenForms = [];
            state.isLoading = false;
            state.isConnecting = false;
            state.error = null;
        },
        selectionClosed(state) {
            state.generation += 1;
            state.accountId = state.tiktokAccount?.id ?? null;
            state.attemptId = null;
            state.availableAdvertisers = [];
            state.isLoading = false;
            state.isConnecting = false;
            state.error = null;
        },
        requestStarted(state, { payload: { scope, connecting } }) {
            if (!sameTikTokScope(state, scope)) return;
            state.isLoading = true;
            state.isConnecting = !!connecting;
            state.error = null;
        },
        discoverySucceeded(state, { payload: { scope, data, field = 'availableAdvertisers' } }) {
            if (!sameTikTokScope(state, scope)) return;
            state[field] = data;
            state.isLoading = false;
            state.isConnecting = false;
        },
        discoveryFailed(state, { payload: { scope, message } }) {
            if (!sameTikTokScope(state, scope)) return;
            state.error = message;
            state.isLoading = false;
            state.isConnecting = false;
        },
        accountReceived(state, { payload: { scope, account } }) {
            if (!sameTikTokScope(state, scope)) return;
            if (state.tiktokAccount?.id !== account?.id || state.tiktokAccount?.connection_version !== account?.connection_version) {
                state.generation += 1;
                state.availableAdvertisers = [];
                state.tiktokAdvertisers = [];
                state.tiktokLeadGenForms = [];
                state.pendingAttempts = [];
                state.attemptId = null;
            }
            state.tiktokAccount = account;
            state.accountId = account?.id ?? null;
            state.isLoading = false;
            state.isConnecting = false;
        },
    },
});
export const { resetTikTokScope, discoveryFailed, discoverySucceeded } = slice.actions;
export const closeTikTokForms = () => slice.actions.formsOpened(null);
export default slice.reducer;

export const resetTikTokSession = (sessionKey = null) => (dispatch, getState) => {
    cancelTikTokRequests();
    dispatch(resetTikTokScope({ sessionKey, practiceId: getState().practiceTreatmentList.practiceId }));
};

const list = (data) => {
    if (Array.isArray(data?.results)) return data.results;
    return Array.isArray(data) ? data : [];
};
const practicePath = (id) => `/api/v1/practices/${id}`;
const accountPath = (p, a) => `${practicePath(p)}/tiktok-accounts/${a}`;
const attemptPath = (p, a) => `${practicePath(p)}/tiktok-setup-attempts/${a}`;
const formPath = (p, a, advertiser) => `${accountPath(p, a)}/tiktok-advertisers/${advertiser}`;

// Every completion, including returned HTTP failures, is checked before state, toast or callbacks.
async function request(dispatch, getState, url, { method = 'GET', data = null, connecting = false, practiceBound = true, isCurrent = () => true } = {}) {
    const scope = selectTikTokScope(getState());
    if (!scope.sessionKey || (practiceBound && !hasFeatureAccess('tiktok-integration', scope.practiceId))) return null;
    const controller = new AbortController();
    controllers.add(controller);
    dispatch(slice.actions.requestStarted({ scope, connecting }));
    try {
        const response = await callApi(url, method, data, false, { signal: controller.signal, suppressServerErrorDispatch: true, maxRetries: 0 });
        if (!sameTikTokScope(scope, selectTikTokScope(getState())) || !isCurrent()) return null;
        if (response.status >= 200 && response.status < 300) return { data: response.data, scope };
        const message = safeTikTokError(response);
        dispatch(discoveryFailed({ scope, message }));
        dispatch(updateError({ success: false, message }));
    } catch (error) {
        if (!sameTikTokScope(scope, selectTikTokScope(getState())) || !isCurrent()) return null;
        const message = safeTikTokError(error);
        dispatch(discoveryFailed({ scope, message }));
        dispatch(updateError({ success: false, message }));
    } finally {
        controllers.delete(controller);
    }
    return null;
}

const validPractice = (getState, practiceId) => selectTikTokScope(getState()).practiceId === practiceId;
const validAccount = (getState, practiceId, accountId) => validPractice(getState, practiceId) && selectTikTokScope(getState()).accountId === accountId;
const validAttempt = (getState, practiceId, attemptId) => validPractice(getState, practiceId) && selectTikTokScope(getState()).attemptId === attemptId;
const succeed = (dispatch, result, field, data) => dispatch(discoverySucceeded({ scope: result.scope, field, data }));
const currentResult = (result, getState) => !!result && sameTikTokScope(result.scope, selectTikTokScope(getState()));

export const openTikTokSelection = ({ practiceId, accountId, attemptId }) => (dispatch, getState) => {
    if (!validPractice(getState, practiceId)) return null;
    cancelTikTokRequests();
    dispatch(slice.actions.selectionOpened({ accountId, attemptId }));
    return selectTikTokScope(getState());
};
export const closeTikTokSelection = (scope) => (dispatch, getState) => {
    if (scope && !sameTikTokScope(scope, selectTikTokScope(getState()))) return;
    cancelTikTokRequests();
    dispatch(slice.actions.selectionClosed());
};

export const getTikTokAccount = (practiceId) => async (dispatch, getState) => {
    if (!validPractice(getState, practiceId)) return;
    const capability = await request(dispatch, getState, `${practicePath(practiceId)}/tiktok-accounts/capabilities/`);
    if (!currentResult(capability, getState)) return;
    succeed(dispatch, capability, 'canManageConnection', capability.data?.can_manage_connection === true);
    const result = await request(dispatch, getState, `${practicePath(practiceId)}/tiktok-accounts/`);
    if (!currentResult(result, getState)) return;
    dispatch(slice.actions.accountReceived({ scope: result.scope, account: list(result.data)[0] || null }));
    if (getState().tiktokIntegration.canManageConnection) {
        const attempts = await request(dispatch, getState, `${practicePath(practiceId)}/tiktok-setup-attempts/`);
        if (currentResult(attempts, getState)) succeed(dispatch, attempts, 'pendingAttempts', list(attempts.data).filter(attempt => attempt.status === 'PENDING_SELECTION'));
    }
};

export const startTikTokSetup = (practiceId) => async (dispatch, getState) => {
    if (!validPractice(getState, practiceId) || !getState().tiktokIntegration.canManageConnection) return;
    dispatch(closeTikTokSelection());
    const result = await request(dispatch, getState, `${practicePath(practiceId)}/tiktok-setup-attempts/`, { method: 'POST', data: {} });
    // auth_url contains state: keep it exclusively in this stack, never in a Redux action.
    if (currentResult(result, getState) && result.data?.auth_url) window.location.assign(result.data.auth_url);
};
export const tiktokConnectUrl = startTikTokSetup;

export const completeTikTokSetup = ({ state, auth_code: authCode }) => async (dispatch, getState) => {
    if (!state || !authCode) return null;
    const result = await request(dispatch, getState, '/api/v1/tiktok/oauth/callback/', { method: 'POST', data: { state, auth_code: authCode }, practiceBound: false });
    if (!currentResult(result, getState)) return null;
    const { practice_id: practiceId, attempt_id: attemptId } = result.data;
    // Only safe context identifiers leave the thunk; inventory is loaded after a clean navigation.
    return { practiceId, attemptId, scope: result.scope };
};

export const getAvailableAdvertisers = (practiceId, accountId) => async (dispatch, getState) => {
    if (!validAccount(getState, practiceId, accountId)) return;
    const result = await request(dispatch, getState, `${accountPath(practiceId, accountId)}/available_advertisers/`);
    if (currentResult(result, getState)) succeed(dispatch, result, 'availableAdvertisers', list(result.data));
};
export const getTikTokSetupAdvertisers = (practiceId, attemptId) => async (dispatch, getState) => {
    if (!validAttempt(getState, practiceId, attemptId)) return;
    const result = await request(dispatch, getState, `${attemptPath(practiceId, attemptId)}/available_advertisers/`);
    if (currentResult(result, getState)) succeed(dispatch, result, 'availableAdvertisers', list(result.data));
};

export const publishTikTokSetup = ({ practiceId, attemptId, advertiserIds }) => async (dispatch, getState) => {
    if (!validAttempt(getState, practiceId, attemptId)) return null;
    const result = await request(dispatch, getState, `${attemptPath(practiceId, attemptId)}/publish/`, { method: 'POST', data: { advertiser_ids: advertiserIds }, connecting: true });
    if (!currentResult(result, getState)) return null;
    cancelTikTokRequests();
    dispatch(slice.actions.accountReceived({ scope: result.scope, account: result.data }));
    dispatch(closeTikTokSelection());
    return { success: true, scope: selectTikTokScope(getState()) };
};
export const selectTikTokAdvertisers = ({ practiceId, accountId, advertiserIds, connectionVersion }) => async (dispatch, getState) => {
    if (!validAccount(getState, practiceId, accountId)) return null;
    const result = await request(dispatch, getState, `${accountPath(practiceId, accountId)}/connect_advertisers/`, { method: 'POST', data: { advertiser_ids: advertiserIds, connection_version: connectionVersion }, connecting: true });
    if (!currentResult(result, getState)) return null;
    cancelTikTokRequests();
    dispatch(slice.actions.accountReceived({ scope: result.scope, account: result.data }));
    dispatch(closeTikTokSelection());
    return { success: true, scope: selectTikTokScope(getState()) };
};

export const disconnectTikTok = (practiceId, accountId, handleSuccess) => async (dispatch, getState) => {
    if (!validAccount(getState, practiceId, accountId)) return;
    const version = getState().tiktokIntegration.tiktokAccount?.connection_version;
    cancelTikTokRequests();
    dispatch(slice.actions.selectionOpened({ accountId }));
    const scope = selectTikTokScope(getState());
    const result = await request(dispatch, getState, `${accountPath(practiceId, accountId)}/disconnect/`, { method: 'POST', data: { connection_version: version } });
    if (!currentResult(result, getState)) {
        if (sameTikTokScope(scope, selectTikTokScope(getState())) && handleSuccess) handleSuccess({ success: false });
        return;
    }
    cancelTikTokRequests();
    dispatch(resetTikTokScope({ sessionKey: result.scope.sessionKey, practiceId }));
    if (handleSuccess) handleSuccess({ success: true });
};

const readAccountData = (practiceId, accountId, path, field) => async (dispatch, getState) => {
    if (!validAccount(getState, practiceId, accountId)) return;
    const result = await request(dispatch, getState, path);
    if (currentResult(result, getState)) succeed(dispatch, result, field, list(result.data));
};
export const getTikTokAdvertisers = (p, a) => readAccountData(p, a, `${accountPath(p, a)}/tiktok-advertisers/`, 'tiktokAdvertisers');
// Form panels have their own identity/generation; changing them must not cancel
// independent account requests, but must reject old lists, errors and refreshes.
const formsGuard = (getState) => {
    const { formsAdvertiserId, formsGeneration } = getState().tiktokIntegration;
    return () => {
        const current = getState().tiktokIntegration;
        return current.formsAdvertiserId === formsAdvertiserId && current.formsGeneration === formsGeneration;
    };
};
export const getTikTokForms = (p, a, adv) => async (dispatch, getState) => {
    if (!validAccount(getState, p, a)) return;
    dispatch(slice.actions.formsOpened(adv));
    const isCurrent = formsGuard(getState);
    const result = await request(dispatch, getState, `${formPath(p, a, adv)}/tiktok-lead-gen-forms/`, { isCurrent });
    if (currentResult(result, getState) && isCurrent()) succeed(dispatch, result, 'tiktokLeadGenForms', list(result.data));
};

const formAction = (p, a, url, followUp, advertiserId = null) => async (dispatch, getState) => {
    if (!validAccount(getState, p, a)) return;
    const isCurrent = advertiserId === null ? () => true : formsGuard(getState);
    const result = await request(dispatch, getState, url, { method: 'POST', isCurrent });
    if (!currentResult(result, getState) || !isCurrent()) return;
    succeed(dispatch, result, 'isLoading', false);
    dispatch(updateError({ success: true, message: 'TikTok forms updated successfully.' }));
    if (followUp && (advertiserId === null || getState().tiktokIntegration.formsAdvertiserId === advertiserId)) await dispatch(followUp);
};
export const syncAllForms = (p, a) => formAction(p, a, `${accountPath(p, a)}/sync_all_forms/`, getTikTokAdvertisers(p, a));
export const syncForms = (p, a, adv) => formAction(p, a, `${formPath(p, a, adv)}/sync_forms/`, getTikTokForms(p, a, adv), adv);
export const syncLeads = (p, a, adv, f) => formAction(p, a, `${formPath(p, a, adv)}/tiktok-lead-gen-forms/${f}/sync_leads/`, null, adv);
export const toggleFormActive = (p, a, adv, f) => formAction(p, a, `${formPath(p, a, adv)}/tiktok-lead-gen-forms/${f}/toggle_active/`, getTikTokForms(p, a, adv), adv);
